/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-17 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';

// Build list features applied for term to show in table
export const buildFeaturesPreview = (productData, searchFeatures, i18n) => {
  // Data from FE API
  const {
    search_range = [],
    numeric_special_character = {},
    partial_search = {},
    typo = {},
    stop_words = {},
    ai_synonym = {},
    // + Synonym data from FE API
    manual_synonyms = {},
    performance_ranking = []
  } = productData;
  // Data from Admin API
  const semantic = _.get(searchFeatures, 'semantic', {});
  // +  Synonym data from Admin API
  const manualSynonym = _.get(searchFeatures, 'manualSynonym', {});
  const merchandising = _.get(searchFeatures, 'merchandising', []);
  const redirects = _.get(searchFeatures, 'redirects', {});

  if (!_.isEmpty(redirects)) {
    return {
      search: [redirects],
      count: 1
    };
  }

  const features = {
    merchandising: [],
    search: [],
    count: 0
  };

  if (merchandising && merchandising.length > 0) {
    features.merchandising = merchandising;
  }

  if (performance_ranking && performance_ranking.length > 0) {
    features.search.push({
      name: 'ranking',
      data: {}
    });
  }

  if (!_.isEmpty(semantic)) {
    features.search.push({
      name: 'semantic',
      data: {}
    });
  }

  if (ai_synonym && !_.isEmpty(ai_synonym)) {
    const aiSynonymTerms = Object.keys(ai_synonym);
    const aiSynonym = aiSynonymTerms
      .map(term => ({
        searchTerm: term,
        biDirection: false,
        synonyms: ai_synonym[term].synonym
      }))
      .filter(data => data.synonyms && data.synonyms.length > 0);

    if (aiSynonym.length > 0)
      features.search.push({
        name: 'aiSynonym',
        data: aiSynonym
      });
  }

  if (manual_synonyms && !_.isEmpty(manual_synonyms) && !_.isEmpty(manualSynonym)) {
    const synonymsData = buildManualSynonyms(manual_synonyms, manualSynonym, i18n);
    features.search.push({
      name: 'manualSynonym',
      data: synonymsData
    });
  }

  if (!_.isEmpty(stop_words)) {
    const afterRemoveStopWord: string = _.get(stop_words, 'afterRemoveStopWord', '');

    const data = buildStopWord(stop_words, typo, afterRemoveStopWord);
    if (_.isEmpty(afterRemoveStopWord)) return data;
    features.search.push(data);
  }

  if (!_.isEmpty(partial_search)) {
    features.search.push({
      name: 'partialSearch',
      data: partial_search
    });
  }

  if (!_.isEmpty(typo)) {
    features.search.push({
      name: 'typoTolerance',
      data: typo
    });
  }
  if (search_range.length > 0 || !_.isEmpty(numeric_special_character)) {
    features.search.push({
      name: 'searchRange',
      data: search_range.length ? search_range[0] : numeric_special_character
    });
  }
  features.count = features.search.length + features.merchandising.length;
  return features;
};

// Build data for stop word to view in table
const buildStopWord = (stop_words, typo, afterRemoveStopWord) => {
  let originalQuery: string = _.get(stop_words, 'originalQuery', '');
  const data = [];
  if (!_.isEmpty(typo)) {
    originalQuery = _.get(typo, 'correctQuery', originalQuery);
    data.push({
      name: 'typoTolerance',
      data: typo
    });
  }
  const originalSplit = originalQuery.split(' ');
  const afterRemoveStopWordSplit = afterRemoveStopWord.split(' ');
  // Find stop word
  const stopWord = originalSplit.filter(word => !afterRemoveStopWordSplit.includes(word)).join(' ');

  data.unshift({
    name: 'stopWord',
    data: {
      word: stopWord
    }
  });
  if (_.isEmpty(afterRemoveStopWord))
    return {
      merchandising: [],
      search: data,
      count: 1
    };
  return {
    name: 'stopWord',
    data: {
      word: stopWord
    }
  };
};

// Build data for manual synonym to view in table
const buildManualSynonyms = (dataFE, dataAdmin, i18n) => {
  const words = Object.keys(dataFE);
  const list = [];
  words.forEach(word => {
    const findSynonym = dataAdmin.filter(data => {
      const condition1 = data.searchTerm === word || data.synonyms.includes(word);
      const condition2 = dataFE[word].includes(data.searchTerm);
      const condition3 = data.synonyms.find(e => dataFE[word].includes(e));
      return condition1 && (condition2 || condition3);
    });
    const listHasDirection = findSynonym.filter(e => e.biDirection);
    const listNoDirection = findSynonym.filter(e => !e.biDirection);

    if (listHasDirection.length > 0 && listNoDirection.length > 0) {
      let dataNoDirectionSynonyms = [];
      listNoDirection.forEach(e => {
        dataNoDirectionSynonyms = dataNoDirectionSynonyms.concat(e.synonyms);
      });

      let viewNoDirectionSynonyms = dataNoDirectionSynonyms;
      if (dataNoDirectionSynonyms.length > 2) {
        const lastText = i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.numberSynonyms', {
          n: dataNoDirectionSynonyms.length - 2
        });
        viewNoDirectionSynonyms = dataNoDirectionSynonyms.slice(0, 2);
        viewNoDirectionSynonyms.push(lastText);
      }
      const dataDirectionSynonyms = dataFE[word].filter(e => !dataNoDirectionSynonyms.includes(e));
      let viewDirectionSynonyms = dataDirectionSynonyms;

      if (dataDirectionSynonyms.length > 2) {
        const lastText = i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.numberSynonyms', {
          n: dataDirectionSynonyms.length - 2
        });
        viewDirectionSynonyms = dataDirectionSynonyms.slice(0, 2);
        viewDirectionSynonyms.push(lastText);
      }
      list.push({
        searchTerm: word,
        synonyms: viewNoDirectionSynonyms,
        biDirection: false
      });
      list.push({
        searchTerm: word,
        synonyms: viewDirectionSynonyms,
        biDirection: true
      });
    } else {
      let synonyms = dataFE[word];
      if (dataFE[word].length > 3) {
        const lastText = i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.numberSynonyms', {
          n: dataFE[word]?.length - 2
        });
        synonyms = synonyms.slice(0, 2);
        synonyms.push(lastText);
      }
      list.push({
        searchTerm: word,
        synonyms,
        biDirection: _.get(findSynonym[0], 'biDirection', false)
      });
    }
  });
  return list;
};

// Build queries for API search preview
export const buildQueriesForSearchFeatures = (
  query: string = '',
  enableAISearch: boolean,
  testSearchData: Object
) => {
  const { manual_synonyms = [], merchandising = [], stop_words = {} } = testSearchData;
  const afterRemoveStopWord: string = _.get(stop_words, 'afterRemoveStopWord', '');
  const typo = _.get(testSearchData, 'typo.correctQuery', null);
  let term = query;
  if (typo) {
    term = typo;
  }
  if (!_.isEmpty(afterRemoveStopWord)) {
    term = afterRemoveStopWord;
  }

  const featuresQueries = {
    q: term?.toLowerCase(),
    originalQuery: query?.toLowerCase()
  };

  if (enableAISearch) {
    featuresQueries.semantic = true;
  }
  if (manual_synonyms && !_.isEmpty(manual_synonyms)) {
    const keys = Object.keys(manual_synonyms);
    featuresQueries.manualSynonym = keys;
  }
  if (merchandising && merchandising.length > 0) {
    // Build words get from merchandising data in FE API
    const merchandisingQueries = merchandising
      .map(element => {
        const query: string = _.get(element, 'query', '');
        if (query === '__all_search__')
          return {
            query: 'all'
          };
        const queryString: string = query.replace('__pfs_equal__', '').trim();
        if (!_.isEmpty(queryString)) {
          return {
            query: queryString,
            createdAt: element.position
          };
        }
        return null;
      })
      .filter(e => e);
    if (merchandisingQueries.length > 0) {
      featuresQueries.merchandising = merchandisingQueries;
    }
  }
  return { featuresQueries, term };
};
